import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Pagination,
  CircularProgress,
  Chip,
  InputAdornment,
  Card,
  CardContent,
  Grid,
  Stack,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import AppLayout from '../../components/AppLayout';
import EditExpenseDialog from '../../components/EditExpenseDialog';
import DeleteConfirmDialog from '../../components/DeleteConfirmDialog';
import API_BASE_URL from '../../config/api';
import { toast } from 'react-toastify';

export default function Expenses() {
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);

  const userRole = localStorage.getItem('role');
  const isAdmin = userRole === 'Admin';

  useEffect(() => {
    fetchExpenses();
  }, [page, searchTerm]);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/api/expenses?page=${page}&limit=10&search=${searchTerm}&sortBy=Year&sortOrder=DESC`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setExpenses(data.expenses);
        setTotalPages(data.pagination.totalPages);
      } else {
        toast.error('Failed to fetch expenses');
      }
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast.error('Error loading expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleAddExpense = () => {
    navigate('/expenses/new');
  };

  const handleEditExpense = (expense) => {
    setSelectedExpense(expense);
    setEditDialogOpen(true);
  };

  const handleDeleteExpense = (expense) => {
    setSelectedExpense(expense);
    setDeleteDialogOpen(true);
  };



  const handleExpenseUpdated = () => {
    setEditDialogOpen(false);
    setSelectedExpense(null);
    fetchExpenses();
    toast.success('Expense updated successfully');
  };

  const handleExpenseDeleted = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/expenses/${selectedExpense.ExpenseID}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        setDeleteDialogOpen(false);
        setSelectedExpense(null);
        fetchExpenses();
        toast.success('Expense deleted successfully');
      } else {
        toast.error('Failed to delete expense');
      }
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Error deleting expense');
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  const getOtherExpensesTotal = (otherExpenses) => {
    if (!otherExpenses || !Array.isArray(otherExpenses)) return 0;
    return otherExpenses.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
  };



  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto', p: { xs: 2, md: 3 } }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          {/* Centered Title */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 500, letterSpacing: 2, color: '#C59A58' }}>
              EXPENSES
            </Typography>
          </Box>

          {/* Search Bar and Add Button in same line */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search by year or month..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#C4A574' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: '60%',
                maxWidth: 500,
                bgcolor: '#262626',
                borderRadius: 1,
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': { borderColor: 'rgba(196,165,116,0.08)' },
                  '&:hover fieldset': { borderColor: 'rgba(196,165,116,0.18)' },
                },
                '& .MuiInputBase-input::placeholder': { color: 'rgba(196,165,116,0.5)' },
              }}
            />

            {isAdmin && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddExpense}
                sx={{
                  bgcolor: '#C4A574',
                  color: '#000',
                  fontWeight: 700,
                  px: 3,
                  py: 1.25,
                  '&:hover': { bgcolor: '#B39565' },
                }}
              >
                ADD EXPENSE
              </Button>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 3, borderColor: 'rgba(255,255,255,0.04)' }} />

        {/* Expenses Cards */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: '#C4A574' }} />
          </Box>
        ) : (
          <>
            {expenses.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" sx={{ color: '#bbb', mb: 1 }}>
                  No expenses found
                </Typography>
                <Typography variant="body2" sx={{ color: '#999' }}>
                  {isAdmin ? 'Click "ADD EXPENSE" to create your first expense record.' : 'No expense records available.'}
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {expenses.map((expense) => (
                  <Grid item key={expense.ExpenseID} xs={12} sm={6} md={4} lg={3}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        transition: 'all 0.25s',
                        bgcolor: '#1f1f1f',
                        border: '1px solid rgba(255,255,255,0.03)',
                        '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 10px 30px rgba(0,0,0,0.6)' },
                      }}
                    >
                      <CardContent sx={{ flex: 1, pt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#C4A574' }}>
                            {expense.FormattedDate}
                          </Typography>
                          {expense.OtherExpenses && expense.OtherExpenses.length > 0 && (
                            <Chip
                              label={`${expense.OtherExpenses.length} other`}
                              size="small"
                              sx={{
                                borderColor: 'rgba(196,165,116,0.12)',
                                color: '#C4A574',
                                bgcolor: 'rgba(196,165,116,0.02)',
                                fontWeight: 700,
                              }}
                            />
                          )}
                        </Box>

                        {/* Payment Information */}
                        <Box
                          sx={{
                            mt: 1,
                            p: 2,
                            bgcolor: '#0f0f0f',
                            borderRadius: 1,
                            border: '1px solid rgba(196,165,116,0.02)',
                          }}
                        >
                          <Box sx={{ textAlign: 'center', mb: 1 }}>
                            <Typography variant="caption" sx={{ color: '#9e9e9e' }}>
                              Total Amount
                            </Typography>
                            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#C59A58' }}>
                              {formatCurrency(expense.TotalAmount)}
                            </Typography>
                          </Box>

                          <Grid container spacing={1} sx={{ mt: 1 }}>
                            <Grid item xs={6}>
                              <Typography variant="caption" sx={{ color: '#9e9e9e' }}>
                                Paid
                              </Typography>
                              <Typography sx={{ fontWeight: 700, color: '#4caf50' }}>
                                {formatCurrency(expense.TotalPaid || 0)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" sx={{ color: '#9e9e9e' }}>
                                Balance
                              </Typography>
                              <Typography sx={{ fontWeight: 700, color: (expense.CurrentBalance || 0) > 0 ? '#d32f2f' : '#4caf50' }}>
                                {formatCurrency(Math.abs(expense.CurrentBalance || 0))}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>

                        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<VisibilityIcon />}
                              onClick={() => navigate(`/expenses/${expense.ExpenseID}`)}
                              fullWidth
                              sx={{
                                borderColor: 'rgba(100,181,246,0.12)',
                                color: '#64b5f6',
                                textTransform: 'none',
                                fontWeight: 700,
                              }}
                            >
                              View
                            </Button>

                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<PaymentIcon />}
                              onClick={() => navigate(`/expenses/${expense.ExpenseID}/payments`)}
                              fullWidth
                              sx={{
                                bgcolor: '#4caf50',
                                color: '#000',
                                '&:hover': { bgcolor: '#45a049' },
                                textTransform: 'none',
                                fontWeight: 700,
                              }}
                            >
                              Payments
                            </Button>
                          </Stack>

                          {isAdmin && (
                            <Stack direction="row" spacing={1}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditExpense(expense);
                                }}
                                fullWidth
                                sx={{
                                  borderColor: '#C4A574',
                                  color: '#C4A574',
                                  textTransform: 'none',
                                  fontWeight: 700,
                                }}
                              >
                                Edit
                              </Button>

                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DeleteIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteExpense(expense);
                                }}
                                fullWidth
                                sx={{
                                  borderColor: '#d32f2f',
                                  color: '#d32f2f',
                                  textTransform: 'none',
                                  fontWeight: 700,
                                }}
                              >
                                Delete
                              </Button>
                            </Stack>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: '#C4A574',
                      '&.Mui-selected': {
                        bgcolor: '#C4A574',
                        color: '#000',
                        fontWeight: 800,
                      },
                      '&:hover': { bgcolor: 'rgba(196,165,116,0.08)' },
                    },
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Dialogs */}
      <EditExpenseDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedExpense(null);
        }}
        expense={selectedExpense}
        onExpenseUpdated={handleExpenseUpdated}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedExpense(null);
        }}
        onConfirm={handleExpenseDeleted}
        title="Delete Expense"
        message={`Are you sure you want to delete the expense for ${selectedExpense?.FormattedDate}? This action cannot be undone.`}
      />
    </AppLayout>
  );
}